import { type FC, useMemo, type RefObject } from "react";
import {
  type UseFormRegister,
  type UseFormWatch,
  type UseFormSetValue,
  type FieldErrors,
} from "react-hook-form";
import { FormItemName } from "@root/shared/components/FormItemName";
import { TextField } from "@root/shared/components/TextField";
import { TextArea } from "@root/shared/components/TextArea";
import { Calendar } from "@root/shared/components/Calendar";
import { countCharacters } from "@root/shared/utils/countCharacters";
import { ErrorInformationText } from "@userFrontend/components/ErrorInformationText";
import { PERSON_IN_CHARGE_NAME_MAX_LENGTH, PERSON_IN_CHARGE_DETAILS_MAX_LENGTH } from "./schema";
import { type ViolationReportInputsValues } from "../../schema";
import * as styles from "./styles.css";

type PersonInChargeProps = {
  register: UseFormRegister<ViolationReportInputsValues>;
  watch: UseFormWatch<ViolationReportInputsValues>;
  setValue: UseFormSetValue<ViolationReportInputsValues>;
  errors: FieldErrors<ViolationReportInputsValues>;
  nameRef?: RefObject<HTMLDivElement>;
  detailsRef?: RefObject<HTMLDivElement>;
};

export const PersonInCharge: FC<PersonInChargeProps> = ({
  register,
  watch,
  setValue,
  errors,
  nameRef,
  detailsRef,
}) => {
  // カレンダーの選択範囲の設定。動的にする必要がないため広めの固定値を入れているだけで、日付に特別な意図はない。
  const startDate = new Date(2000, 1, 1);
  const endDate = new Date(2100, 12, 31);

  const nameValue = watch("personInChargeName") || "";
  const detailsValue = watch("personInChargeDetails") || "";
  const employmentDateValue = watch("employmentDate");

  const nameCount = useMemo(() => {
    return countCharacters(nameValue);
  }, [nameValue]);

  const detailsCount = useMemo(() => {
    return countCharacters(detailsValue);
  }, [detailsValue]);

  const isNameOverLimit = nameCount > PERSON_IN_CHARGE_NAME_MAX_LENGTH;
  const isDetailsOverLimit = detailsCount > PERSON_IN_CHARGE_DETAILS_MAX_LENGTH;

  return (
    <div className={styles.wrapper}>
      <div ref={nameRef}>
        <FormItemName labelRequirementType="optional">担当者</FormItemName>
      </div>
      <div className={styles.inputArea}>
        <TextField
          {...register("personInChargeName")}
          placeholder="例）ヤマダさん"
          hasError={isNameOverLimit || !!errors.personInChargeName}
        />
        <div className={styles.counterText}>
          <p>
            <span
              role="status"
              aria-live="polite"
              className={isNameOverLimit ? styles.textOverLimit : ""}
            >
              {nameCount}
            </span>
            /{PERSON_IN_CHARGE_NAME_MAX_LENGTH}文字以内
          </p>
        </div>
        {errors.personInChargeName?.message && (
          <ErrorInformationText text={errors.personInChargeName.message} />
        )}
      </div>
      <div className={styles.section} ref={detailsRef}>
        <FormItemName labelRequirementType="optional">
          詳細について（担当者の対応、状況など）
        </FormItemName>
        <div className={styles.inputArea}>
          <TextArea
            {...register("personInChargeDetails")}
            placeholder="採用担当者の対応など"
            size="large"
            hasError={isDetailsOverLimit || !!errors.personInChargeDetails}
          />
          <div className={styles.counterText}>
            <p>
              <span
                role="status"
                aria-live="polite"
                className={isDetailsOverLimit ? styles.textOverLimit : ""}
              >
                {detailsCount}
              </span>
              /{PERSON_IN_CHARGE_DETAILS_MAX_LENGTH}文字以内
            </p>
          </div>
          {errors.personInChargeDetails?.message && (
            <ErrorInformationText text={errors.personInChargeDetails.message} />
          )}
        </div>
      </div>
      <div className={styles.section}>
        <FormItemName labelRequirementType="optional">入社日</FormItemName>
        <div className={styles.inputArea}>
          <Calendar
            selectedDate={employmentDateValue}
            handleOnSelect={(date) => setValue("employmentDate", date)}
            placeholder="0000年00月00日"
            name="employmentDate"
            startDate={startDate}
            endDate={endDate}
            hasError={!!errors.employmentDate}
          />
          {errors.employmentDate?.message && (
            <ErrorInformationText text={errors.employmentDate.message} />
          )}
        </div>
      </div>
    </div>
  );
};
